export const DATE_FORMAT_VALUES = {
  DAY_FIRST: 'DD/MM/YYYY',
  MONTH_FIRST: 'MM/DD/YYYY',
} as const

export type DateFormatValue = (typeof DATE_FORMAT_VALUES)[keyof typeof DATE_FORMAT_VALUES]

export const DATE_FORMAT_SCHEMA_KEYS = {
  DAY_FIRST: 'DAY_FIRST',
  MONTH_FIRST: 'MONTH_FIRST',
} as const

export type DateFormatStyle =
  | 'short'
  | 'medium'
  | 'long'
  | 'full'
  | 'fullWithDay'
  | 'fullWithDayAndTime'
  | 'mediumWithTime'
  | 'mediumWithTimeAt'

const getDateFormatPatterns = (): Record<
  DateFormatStyle,
  { monthFirst: string; dayFirst: string }
> => {
  const t = getTimePattern()
  return {
    short: { monthFirst: 'MM/dd/yyyy', dayFirst: 'dd/MM/yyyy' },
    medium: { monthFirst: 'MMM d, yyyy', dayFirst: 'd MMM yyyy' },
    long: { monthFirst: 'MMMM d, yyyy', dayFirst: 'd MMMM yyyy' },
    full: { monthFirst: 'EEEE, MMMM d', dayFirst: 'EEEE, d MMMM' },
    fullWithDay: { monthFirst: 'EEE, MMM d, yyyy', dayFirst: 'EEE, d MMM yyyy' },
    fullWithDayAndTime: {
      monthFirst: `EEE, MMM d, yyyy, ${t}`,
      dayFirst: `EEE, d MMM yyyy, ${t}`,
    },
    mediumWithTime: { monthFirst: `MMM d, yyyy, ${t}`, dayFirst: `d MMM yyyy, ${t}` },
    mediumWithTimeAt: {
      monthFirst: `MMM dd, yyyy 'at' ${t}`,
      dayFirst: `dd MMM yyyy 'at' ${t}`,
    },
  }
}

export const SITE_DATE_FORMAT_KEY = 'site-date-format'
export const SITE_TIME_FORMAT_KEY = 'site-time-format'
export const SITE_NUMBER_FORMAT_KEY = 'site-number-format'
export const TIME_FORMAT_24H = '24-hour'

export const getDateFormatPattern = (
  dateFormat: string | undefined,
  style: DateFormatStyle = 'short'
): string => {
  const patterns = getDateFormatPatterns()
  const isDayFirst =
    dateFormat === DATE_FORMAT_VALUES.DAY_FIRST || dateFormat === DATE_FORMAT_SCHEMA_KEYS.DAY_FIRST
  return isDayFirst ? patterns[style].dayFirst : patterns[style].monthFirst
}

/**
 * Normalize a stored `dateFormat` value to the literal "MM/DD/YYYY" / "DD/MM/YYYY"
 * form the settings UI dropdown works with.
 *
 * Legacy sites still hold the schema-key values (`'DAY_FIRST'` / `'MONTH_FIRST'`)
 * from site-settings-defaults.ts. The settings dialog options are the literal
 * "MM/DD/YYYY" / "DD/MM/YYYY" form, so without this helper the dropdown
 * silently falls back to the first option for legacy sites — making it appear
 * MM/DD/YYYY is selected when the DB actually holds DAY_FIRST. See CORE-864.
 *
 * Unrecognized / falsy values fall back to "MM/DD/YYYY" to match the dropdown's
 * first-option default.
 */
export const normalizeStoredDateFormat = (
  dateFormat: string | null | undefined
): DateFormatValue => {
  if (
    dateFormat === DATE_FORMAT_VALUES.DAY_FIRST ||
    dateFormat === DATE_FORMAT_SCHEMA_KEYS.DAY_FIRST
  ) {
    return DATE_FORMAT_VALUES.DAY_FIRST
  }
  // MONTH_FIRST (canonical), the legacy MONTH_FIRST schema key, and any unrecognised
  // or falsy value all collapse to the MONTH_FIRST literal — matches the dropdown's
  // first-option default.
  return DATE_FORMAT_VALUES.MONTH_FIRST
}

const safeLocalStorageGet = (key: string): string | null => {
  if (typeof localStorage === 'undefined') return null
  return localStorage.getItem(key)
}

export const getSiteDateFormatSetting = (): string | undefined =>
  safeLocalStorageGet(SITE_DATE_FORMAT_KEY) ?? undefined

export const getSiteTimeFormatSetting = (): string | null =>
  safeLocalStorageGet(SITE_TIME_FORMAT_KEY)

export const isSite12HourFormat = (): boolean => getSiteTimeFormatSetting() !== TIME_FORMAT_24H

export const getTimePattern = (): string => (isSite12HourFormat() ? 'h:mm a' : 'HH:mm')

export const formatTimeString = (timeStr: string | null | undefined): string => {
  if (!timeStr) return ''
  const parts = timeStr.split(':').map(Number)
  const hours = parts[0] ?? 0
  const minutes = parts[1] ?? 0
  if (isSite12HourFormat()) {
    const period = hours >= 12 ? 'PM' : 'AM'
    const h12 = hours % 12 || 12
    return `${h12}:${String(minutes).padStart(2, '0')} ${period}`
  }
  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`
}

export const getSiteNumberFormatSetting = (): string | undefined =>
  safeLocalStorageGet(SITE_NUMBER_FORMAT_KEY) || undefined

export const getSiteDateFormat = (style: DateFormatStyle = 'medium'): string =>
  getDateFormatPattern(getSiteDateFormatSetting(), style)
